import { Button, TextField, Autocomplete, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from "@mui/icons-material/Close";
import {
  AdminItemsRow,
  AdminItemsContainer,
  AdminItemContainer,
  AdminInviteDetailsContainer,
  AdminItemContainerForm,
  AdminItemContainerChip,
} from "../../admin.styled";
import { Invite } from "../../admin.type";
import { useState } from "react";
import { formatDate } from "core/lib/utils";
import { deleteUser } from "modules/agentMySecurity/services/embeddingsService";
import { ConfirmationDialog } from "core/components/global/confirmationDialogBox/confirmationDialog";
import { Toast } from "core/components/global/toast/toast";
import { useNavigate } from "react-router-dom";



export default function AdminGroup({
  user,
  amsUser,
  handleUpdateUser,
  availableGroups,
  onDelete
}) {
    const [openRow, setRowOpen] = useState(false);
    const [username, setUsername] = useState<string>(user.username);
    const [email, setEmail] = useState<string>(user.email);
    const [groups, setGroups] = useState<string[]>(user.groups);
    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const navigate = useNavigate();

    const preHandleUpdateInvite = () => {
        handleUpdateUser({
            userId: user._id,
            groups: groups,
            email: email,
            username: username
        })
    }

    const navigateToConversations = () => {
        navigate("/console/secai/admin/conversations", { state: { ID: user._id } });
    }

    // Locally update the list of invites
    const handleInviteChange = (currentInvite: Invite, field: string, value: any) => {
        if (field === "username") {
            setUsername(value);
        } else if (field === "email") {
            setEmail(value);
        } else if (field === "groups") {
            setGroups(value);
        }
    };

    const handleRemoveGroup = (groupName: string) => {
        setGroups(groups.filter((group) => group !== groupName))
    }

    const handleDeleteClick = () => {
        setOpenDialog(true);
    };

    const handleDelete = async () => {
        try {
            setIsDeleting(true);
            const response = await deleteUser(user._id);
            
            if (response !== null) {
                onDelete(user._id);
                Toast("User deleted successfully", "success");
            } else {
                Toast("Failed to delete user", "error");
            }
        } catch (error) {
            console.error("Delete error:", error);
            Toast("Error deleting user", "error");
        } finally {
            setIsDeleting(false);
            setOpenDialog(false);
        }
    };

    return (
        <AdminItemsRow>
            <div className="unexpanded-row-container">
                <div className="row-items" onClick={() => setRowOpen(!openRow)}>
                    <p className="row-item">{user.username}</p>
                    <p className="row-item">{user.email}</p>
                    <p className="row-item">{formatDate(user.createdAt)}</p>
                </div>
                <div className="row-buttons">
                    <IconButton onClick={handleDeleteClick}>
                        <DeleteOutlineOutlinedIcon fontSize="small" color="error"/>
                    </IconButton>
                </div>
            </div>

            {openRow && (
                <AdminItemsContainer>
                    <AdminInviteDetailsContainer>
                        <AdminItemContainer>
                            <strong>Username:</strong>
                            <TextField
                                value={username}
                                onChange={(e) =>
                                    handleInviteChange(user, "username", e.target.value)
                                }
                                variant="outlined"
                                fullWidth
                                disabled={amsUser?.email === user.email ? true : false}
                            />
                        </AdminItemContainer>
                        <AdminItemContainer>
                            <strong>Email:</strong>
                            <TextField
                                value={email}
                                onChange={(e) =>
                                    handleInviteChange(user, "email", e.target.value)
                                }
                                variant="outlined"
                                fullWidth
                                disabled={amsUser?.email === user.email ? true : false}
                            />
                        </AdminItemContainer>
                        <AdminItemContainer>
                            <strong>Created:</strong>
                            <TextField
                                value={formatDate(user.createdAt)}
                                variant="outlined"
                                fullWidth
                            />
                        </AdminItemContainer>
                        <AdminItemContainer>
                            <strong>Last Updated:</strong>
                            <TextField
                                value={formatDate(user.updatedAt)}
                                variant="outlined"
                                fullWidth
                            />
                        </AdminItemContainer>
                        <AdminItemContainer>
                            <strong>Groups:</strong>
                            <AdminItemContainerForm fullWidth>
                                <Autocomplete
                                    multiple
                                    disabled={amsUser?.email === user.email ? true : false}
                                    value={groups}
                                    onChange={(event, newGroups) => handleInviteChange(user, "groups", newGroups)}
                                    options={availableGroups}
                                    getOptionLabel={(option) => option}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderTags={(inputed) =>
                                        inputed.map((option, index) => (
                                            <AdminItemContainerChip
                                                className="m-1"
                                                label={option}
                                                key={`update-user-list-index-${option}`}
                                                onDelete={() => handleRemoveGroup(option)}
                                                deleteIcon={<CloseIcon />}
                                                variant="outlined"
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label=""
                                            className="custom-input"
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </AdminItemContainerForm>
                        </AdminItemContainer>
                    </AdminInviteDetailsContainer>
                    <Button
                        color="primary"
                        onClick={() => preHandleUpdateInvite()}
                        variant="outlined"
                        className="mb-3"
                    >
                        Update User
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => navigateToConversations()}
                        variant="outlined"
                        className="mb-3"
                        style={{
                                position: 'relative',
                                left: '1em'
                        }}
                    >
                        View Conversations
                    </Button>
                </AdminItemsContainer>
            )}

            <ConfirmationDialog
                open={openDialog}
                title="Confirm Delete"
                message={`Are you sure you want to delete user "${user.username}"?`}
                isLoading={isDeleting}
                onClose={() => setOpenDialog(false)}
                onConfirm={handleDelete}
            />
        </AdminItemsRow>
    );
}
